import { useState } from 'react';
import AllergyIcon from 'components/atoms/icons/allergyIcon';
import BaseButton from 'components/atoms/baseButton';
import { useTranslation } from 'react-i18next';
import { useAddAllergyLog } from 'infrastructure/hooks/ccm';
import { isObjectValid } from 'infrastructure/functions';
import AllergyLogForm from 'components/molecules/admin-panel/forms/allergy-log';
import { usePatientInfo } from 'infrastructure/hooks/patient/use-patient-info';
import { isCcmActionAllowedStatus } from 'utils/userTypeHelper';

import AllergyLogTable from './components/allergy-table';
import s from './styles.module.scss';
import DraggableModal from '../draggableModal';

import type { Values } from 'components/molecules/admin-panel/forms/allergy-log/validation-schema';
import type { FC } from 'react';

interface PatientAllergyLogProps {
  patientGuid: string;
  disableActions?: boolean;
}

const PatientAllergyLog: FC<PatientAllergyLogProps> = ({
  patientGuid,
  disableActions = false,
}) => {
  const [page, setPage] = useState(1);
  const [open, setOpen] = useState(false);
  const { t } = useTranslation();
  const { addAllergyLog, addLoading } = useAddAllergyLog();
  const { patientInfo, loading } = usePatientInfo({ patientGuid });
  const disableButton = !isCcmActionAllowedStatus(patientInfo?.ccmStatus);

  const toggleOpen = () => setOpen((prev) => !prev);

  const onSubmit = async (values: Values) => {
    if (isObjectValid(values))
      addAllergyLog({ ...values, patientGuid }, { onSuccess: toggleOpen });
  };

  const onCancelForm = () => {
    toggleOpen();
  };

  return (
    <>
      <div className={s.wrapper} id="allergy-history">
        <div className={s['title-wrapper']}>
          <h4 className={s.title} data-cy="allergy-log-section">
            <AllergyIcon />
            {t('labels.allergyLog')}
          </h4>
          <BaseButton
            label={`+ ${t('labels.addAllergy')}`}
            dataCy="addAllergy-button"
            onClick={toggleOpen}
            loading={addLoading}
            data-cy="add-allergy-log-btn"
            disabled={disableActions || disableButton || loading}
          />
        </div>
        <div className={s.divider} />
        <AllergyLogTable
          patientGuid={patientGuid}
          page={page}
          setPage={setPage}
          disableActions={disableActions || disableButton}
        />
      </div>
      {open && (
        <DraggableModal
          title={t('labels.addAllergy')}
          isOpen={open}
          toggle={toggleOpen}
          bodyClassName={s['modal-body']}
          modalClassName={s.modal}
          footerClassName={s['modal-footer']}
        >
          <AllergyLogForm
            loading={addLoading}
            onSubmit={onSubmit}
            onCancelForm={onCancelForm}
          />
        </DraggableModal>
      )}
    </>
  );
};
export default PatientAllergyLog;
